import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Avatar, Select, MenuItem, Stack, Button, TextField, Alert
} from '@mui/material';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import GppBadRoundedIcon from '@mui/icons-material/GppBadRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadIdentity, changeUserStatus, createIdentityUser } from './identitySlice';
import { severityChipSx, severityLabel, formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = {
  active: tokens.severity.low,
  locked: tokens.severity.medium,
  suspended: tokens.severity.critical,
  pending_activation: tokens.severity.info
};

export default function IdentityPage() {
  const dispatch = useDispatch();
  const { users, alerts, policy, status, error } = useSelector((s) => s.identity);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [role, setRole] = useState('VIEWER');

  useEffect(() => { dispatch(loadIdentity()); }, [dispatch]);

  const mfaCoverage = users.length ? Math.round((users.filter((u) => u.mfaEnabled).length / users.length) * 100) : 0;
  const privilegedCount = users.filter((u) => u.privileged).length;
  const handleCreate = () => {
    if (!email.trim() || !fullName.trim() || temporaryPassword.length < 14) return;
    dispatch(createIdentityUser({ email: email.trim(), fullName: fullName.trim(), temporaryPassword, roles: [role] }));
    setEmail(''); setFullName(''); setTemporaryPassword(''); setRole('VIEWER');
  };

  return (
    <Box>
      <PageHeader
        title="User & Identity Security"
        subtitle="MFA coverage, role-based access, session activity, and privileged account monitoring"
      />

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <StatCard label="MFA Coverage" value={`${mfaCoverage}%`} icon={VerifiedUserRoundedIcon} accent={mfaCoverage >= 80 ? tokens.severity.low : tokens.severity.medium} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Privileged Accounts" value={privilegedCount} icon={GppBadRoundedIcon} accent={tokens.severity.info} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Identity Alerts (24h)" value={alerts.length} accent={tokens.severity.high} />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Create User</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
          <TextField size="small" label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <TextField size="small" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField size="small" label="Temporary password" type="password" helperText="Minimum 14 characters" value={temporaryPassword} onChange={(e) => setTemporaryPassword(e.target.value)} />
          <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} sx={{ minWidth: 150 }}>
            {['VIEWER', 'SOC_ANALYST', 'DETECTION_ENGINEER', 'THREAT_INTEL_ANALYST', 'ORG_ADMIN', 'SUPER_ADMIN'].map((r) => <MenuItem key={r} value={r}>{r.replace(/_/g, ' ')}</MenuItem>)}
          </Select>
          <Button variant="contained" disabled={status === 'loading' || !email.trim() || !fullName.trim() || temporaryPassword.length < 14} onClick={handleCreate}>Add User</Button>
        </Stack>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Users</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['User', 'Role', 'MFA', 'Last Login', 'Status'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', bgcolor: tokens.brand.dim }}>
                          {u.name.split(' ').map((n) => n[0]).join('')}
                        </Avatar>
                        <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{u.name}</Typography>
                        {u.privileged && <Chip label="privileged" size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: tokens.bg.elevated, color: tokens.severity.info }} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.secondary }}>{u.role}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.mfaEnabled ? 'enabled' : 'disabled'} size="small"
                        sx={{ color: u.mfaEnabled ? tokens.severity.low : tokens.severity.critical, border: `1px solid ${u.mfaEnabled ? tokens.severity.low : tokens.severity.critical}`, backgroundColor: 'transparent' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: tokens.text.muted }}>{formatRelativeTime(u.lastLogin)}</TableCell>
                    <TableCell>
                      <Select
                        size="small" value={u.status}
                        onChange={(e) => dispatch(changeUserStatus({ id: u.id, status: e.target.value }))}
                        sx={{ fontSize: '0.72rem', color: STATUS_COLOR[u.status], minWidth: 110 }}
                      >
                        {['active', 'locked', 'suspended', 'pending_activation'].map((s) => (
                          <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem', textTransform: 'capitalize' }}>{s.replace(/_/g, ' ')}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Identity Alerts</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {alerts.map((a) => (
                <Box key={a.id} sx={{ p: 1.3, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary, fontWeight: 500 }}>{a.type}</Typography>
                    <Chip label={severityLabel(a.severity)} size="small" sx={severityChipSx(a.severity)} />
                  </Box>
                  <Typography sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{a.detail}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.text.muted, fontFamily: '"JetBrains Mono", monospace' }}>
                    {a.user} · {formatRelativeTime(a.timestamp)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          {policy && (
            <Paper sx={{ p: 2.4 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Password Policy</Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {[
                  ['Minimum length', `${policy.minLength} characters`],
                  ['MFA required', policy.requireMfa ? 'Yes' : 'No'],
                  ['Rotation', `${policy.rotationDays} days`],
                  ['Lockout threshold', `${policy.lockoutThreshold} attempts`],
                  ['Breach database check', policy.breachCheckEnabled ? 'Enabled' : 'Disabled']
                ].map(([label, val]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: tokens.text.secondary, fontSize: '0.8rem' }}>{label}</Typography>
                    <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: '0.8rem', fontFamily: '"JetBrains Mono", monospace' }}>{val}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
